import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || '';

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    sessionToken: null,
    deviceKey: null,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    initAuth();
  }, []);

  const initAuth = async () => {
    // Check for existing session first
    const storedSession = sessionStorage.getItem('iptv_session');
    if (storedSession) {
      try {
        const { sessionToken, deviceKey } = JSON.parse(storedSession);

        // Verify session is still valid
        const isValid = await verifySession(sessionToken);
        if (isValid) {
          // Set token on API service immediately
          const { api } = await import('../services/api');
          api.setSessionToken(sessionToken);

          setAuth({
            isAuthenticated: true,
            sessionToken,
            deviceKey,
            isLoading: false,
            error: null
          });
          return;
        }
      } catch (e) {
        sessionStorage.removeItem('iptv_session');
      }
    }

    // Check URL for credentials (from QR code scan)
    const params = new URLSearchParams(window.location.search);
    const deviceKey = params.get('device_key');
    const mac = params.get('mac');
    const token = params.get('token');

    if (deviceKey && mac && token) {
      // Clean URL immediately (before API call)
      window.history.replaceState({}, '', window.location.pathname);

      try {
        // Exchange credentials for session token
        const response = await fetch(`${API_BASE}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_key: deviceKey, mac, token })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Authentication failed');
        }

        const data = await response.json();

        // Store session
        sessionStorage.setItem('iptv_session', JSON.stringify({
          sessionToken: data.sessionToken,
          deviceKey
        }));

        // Set token on API service immediately (before state update triggers child renders)
        const { api } = await import('../services/api');
        api.setSessionToken(data.sessionToken);

        setAuth({
          isAuthenticated: true,
          sessionToken: data.sessionToken,
          deviceKey,
          isLoading: false,
          error: null
        });
      } catch (error) {
        setAuth({
          isAuthenticated: false,
          sessionToken: null,
          deviceKey: null,
          isLoading: false,
          error: error.message || 'Authentication failed'
        });
      }
    } else {
      // No credentials in URL or storage
      setAuth({
        isAuthenticated: false,
        sessionToken: null,
        deviceKey: null,
        isLoading: false,
        error: null
      });
    }
  };

  const verifySession = async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const logout = () => {
    sessionStorage.removeItem('iptv_session');
    setAuth({
      isAuthenticated: false,
      sessionToken: null,
      deviceKey: null,
      isLoading: false,
      error: null
    });
  };

  return (
    <AuthContext.Provider value={{ ...auth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
